import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Mail, Plus, Trash2, Send, Copy, CheckCircle, Shield, User } from 'lucide-react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api, useAuth } from '../../lib/auth';
import { formatDateTime, cn } from '../../lib/utils';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';

export default function AdminTeam() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [teamMembers, setTeamMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    name: '',
    department_ids: []
  });
  const [createForm, setCreateForm] = useState({
    email: '',
    name: '',
    password: '',
    role: 'pod_team_member',
    department_ids: []
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, invitesRes, deptsRes] = await Promise.all([
        api.getUsers(),
        api.getTeamInvites(),
        api.getDepartments()
      ]);
      setTeamMembers(usersRes.data.filter(u => ['team_member', 'pod_team_member', 'admin'].includes(u.role)));
      setInvites(invitesRes.data || []);
      setDepartments(deptsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeamMember = async () => {
    if (!createForm.email || !createForm.name || !createForm.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.createTeamMember(createForm);
      toast.success('Team member created successfully!');
      setShowCreateDialog(false);
      setCreateForm({ email: '', name: '', password: '', role: 'pod_team', department_ids: [] });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create team member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteForm.email || !inviteForm.name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await api.sendTeamInvite(inviteForm);
      toast.success('Invite sent successfully!');
      setShowInviteDialog(false);
      setInviteForm({ email: '', name: '', department_ids: [] });
      loadData();
      
      // Show invite link for manual sharing
      if (response.data.invite_link) {
        toast.info(
          <div>
            <p className="mb-2">Invite link (also sent via email):</p>
            <code className="text-xs bg-white/10 p-1 rounded">{response.data.invite_link}</code>
          </div>,
          { duration: 10000 }
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyInviteLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Invite link copied to clipboard');
  };

  const handleResendInvite = async (inviteId) => {
    try {
      await api.resendTeamInvite(inviteId);
      toast.success('Invite resent');
    } catch (error) {
      toast.error('Failed to resend invite');
    }
  };

  const handleCancelInvite = async (inviteId) => {
    if (!window.confirm('Cancel this invite?')) return;
    try {
      await api.cancelTeamInvite(inviteId);
      toast.success('Invite cancelled');
      loadData();
    } catch (error) {
      toast.error('Failed to cancel invite');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await api.updateUserRole(userId, newRole);
      toast.success('Role updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div>
            <h1 className="font-heading text-3xl font-bold text-white mb-2">
              Team Management
            </h1>
            <p className="text-gray-400">
              {isAdmin ? 'Add and manage team members' : 'View team members'}
            </p>
          </div>
          
          {/* Only Admins can add team members */}
          {isAdmin && (
          <div className="flex gap-2">
            {/* Add Team Member Manually */}
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="btn-secondary">
                  <User className="w-4 h-4 mr-2" />
                  Add Manually
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-white/10 max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-white">Add Team Member</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Name *</label>
                    <Input
                      value={createForm.name}
                      onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                      placeholder="John Doe"
                      className="input-dark"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Email *</label>
                    <Input
                      type="email"
                      value={createForm.email}
                      onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                      placeholder="john@example.com"
                      className="input-dark"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Password *</label>
                    <Input
                      type="password"
                      value={createForm.password}
                      onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                      placeholder="••••••••"
                      className="input-dark"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Role *</label>
                    <Select 
                      value={createForm.role} 
                      onValueChange={(value) => setCreateForm({ ...createForm, role: value })}
                    >
                      <SelectTrigger className="input-dark">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-white/10">
                        <SelectItem value="pod_team_member">POD Team Member</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Assign to Department (Optional)</label>
                    <Select 
                      value={createForm.department_ids[0] || 'none'} 
                      onValueChange={(value) => setCreateForm({ ...createForm, department_ids: value === 'none' ? [] : [value] })}
                    >
                      <SelectTrigger className="input-dark">
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-white/10 max-h-60">
                        <SelectItem value="none">None</SelectItem>
                        {departments.map((dept) => (
                          <SelectItem key={dept.id} value={dept.id}>
                            {dept.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 pt-4">
                    <Button
                      onClick={handleCreateTeamMember}
                      disabled={submitting || !createForm.email || !createForm.name || !createForm.password}
                      className="btn-primary flex-1"
                    >
                      {submitting ? 'Creating...' : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Create Account
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          )}
        </motion.div>

        {/* Team Members */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass rounded-2xl overflow-hidden"
        >
          <div className="p-6 border-b border-white/10">
            <h2 className="font-semibold text-white flex items-center gap-2">
              <Users className="w-5 h-5" />
              Team Members ({teamMembers.length})
            </h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading...</div>
          ) : teamMembers.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No team members yet. Send an invite to get started!</div>
          ) : (
            <div className="divide-y divide-white/10">
              {teamMembers.map((member) => (
                <div 
                  key={member.id}
                  className="p-4 hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center font-bold",
                        member.role === 'admin' ? "bg-accent/20 text-accent" : "bg-primary/20 text-primary"
                      )}>
                        {member.name?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className="font-medium text-white">{member.name}</p>
                        <p className="text-sm text-gray-400">{member.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {/* Only Admin users can change roles */}
                      {user?.role === 'admin' && (
                      <Select 
                        value={member.role} 
                        onValueChange={(value) => handleUpdateRole(member.id, value)}
                      >
                        <SelectTrigger className="input-dark w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-white/10">
                          <SelectItem value="admin">
                            <span className="flex items-center gap-2">
                              <Shield className="w-3 h-3 text-accent" />
                              Admin
                            </span>
                          </SelectItem>
                          <SelectItem value="pod_team_member">
                            <span className="flex items-center gap-2">
                              <Users className="w-3 h-3 text-blue-400" />
                              POD Team Member
                            </span>
                          </SelectItem>
                          <SelectItem value="team_member">
                            <span className="flex items-center gap-2">
                              <User className="w-3 h-3 text-gray-400" />
                              Team Member
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      )}
                      <span className={cn(
                        "px-2 py-1 rounded-full text-xs font-medium capitalize",
                        member.role === 'admin' ? "bg-accent/20 text-accent" :
                        member.role === 'pod_team_member' ? "bg-blue-500/20 text-blue-400" :
                        "bg-gray-500/20 text-gray-400"
                      )}>
                        {member.role?.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
